import { useEffect, useRef, useState } from 'react';

export default function useArtworkSwiper({
  activeSlide,
  artworkCount,
  setActiveSlide,
  useNativeScroll,
}) {
  const swiperRef = useRef(null);
  const nativeScrollCommitTimerRef = useRef(null);
  const positionFrameRef = useRef(null);
  const positionObserverRef = useRef(null);
  const pendingPositionRef = useRef(null);
  const nativeCommittedSlideRef = useRef(null);
  const previousModeRef = useRef(useNativeScroll);
  const previousActiveSlideRef = useRef(activeSlide);
  const [visibleSlideIndex, setVisibleSlideIndex] = useState(activeSlide ?? 0);

  function cancelNativeCommit() {
    window.clearTimeout(nativeScrollCommitTimerRef.current);
    nativeScrollCommitTimerRef.current = null;
  }

  function cancelPendingPosition() {
    if (positionFrameRef.current !== null) {
      window.cancelAnimationFrame(positionFrameRef.current);
      positionFrameRef.current = null;
    }
    positionObserverRef.current?.disconnect();
    positionObserverRef.current = null;
    pendingPositionRef.current = null;
  }

  function positionNativeSlide(swiper) {
    const target = pendingPositionRef.current;
    if (target === null || !swiper || swiper.destroyed) return;

    // React renders virtual slides after Swiper emits virtualUpdate. Wait for
    // the target to exist before positioning native scroll once.
    const targetSlide = Array.from(swiper.wrapperEl?.children ?? []).find(
      (slide) => Number(slide.dataset.swiperSlideIndex) === target,
    );
    if (!targetSlide || !Number.isFinite(swiper.snapGrid?.[target])) return;

    cancelPendingPosition();
    swiper.slideTo(target, 0, false);
    setVisibleSlideIndex(target);
  }

  function scheduleNativePosition(swiper) {
    if (positionFrameRef.current !== null || pendingPositionRef.current === null) return;
    positionFrameRef.current = window.requestAnimationFrame(() => {
      positionFrameRef.current = null;
      positionNativeSlide(swiper);
    });
  }

  function moveToSlide(swiper, index) {
    if (!swiper || swiper.destroyed) return;
    cancelNativeCommit();
    cancelPendingPosition();

    if (!useNativeScroll) {
      if (swiper.activeIndex !== index) swiper.slideTo(index, 0, false);
      setVisibleSlideIndex(index);
      return;
    }

    if (swiper.activeIndex === index) {
      setVisibleSlideIndex(index);
      return;
    }

    pendingPositionRef.current = index;
    setVisibleSlideIndex(index);
    if (swiper.wrapperEl && typeof MutationObserver !== 'undefined') {
      positionObserverRef.current = new MutationObserver(() => scheduleNativePosition(swiper));
      positionObserverRef.current.observe(swiper.wrapperEl, { childList: true });
    }
    // cssMode does not update the virtual range when slideTo is called.
    swiper.updateActiveIndex(index);
    swiper.virtual?.update(true);
    scheduleNativePosition(swiper);
  }

  useEffect(() => {
    const activeSlideChanged = previousActiveSlideRef.current !== activeSlide;
    previousActiveSlideRef.current = activeSlide;
    if (previousModeRef.current !== useNativeScroll) {
      previousModeRef.current = useNativeScroll;
      cancelNativeCommit();
    }

    const swiper = swiperRef.current;
    if (!swiper || swiper.destroyed) return;

    // Native scroll reports its settled slide through activeSlide. Only an
    // external selection should reposition the scroller.
    if (!activeSlideChanged) return;
    const fromNativeScroll = useNativeScroll && nativeCommittedSlideRef.current === activeSlide;
    nativeCommittedSlideRef.current = null;
    if (fromNativeScroll) return;

    const maxIndex = Math.max((artworkCount ?? 1) - 1, 0);
    const target = Math.min(Math.max(activeSlide ?? 0, 0), maxIndex);
    if (swiper.activeIndex !== target || pendingPositionRef.current !== null) {
      moveToSlide(swiper, target);
    }
  }, [activeSlide, artworkCount, useNativeScroll]);

  useEffect(() => () => {
    cancelNativeCommit();
    cancelPendingPosition();
  }, []);

  function onInit(swiper) {
    if (useNativeScroll && typeof swiper.destroy === 'function') {
      // Swiper 8 restores cssMode scroll snapping in a frame after slideTo.
      // Keep its wrapper available if the breakpoint unmounts it first.
      const destroy = swiper.destroy.bind(swiper);
      swiper.destroy = (_deleteInstance, cleanStyles) => destroy(false, cleanStyles);
    }
    swiperRef.current = swiper;
    moveToSlide(swiper, activeSlide ?? 0);
  }

  function onVirtualUpdate(swiper) {
    if (useNativeScroll) scheduleNativePosition(swiper);
  }

  function onSlideChange(swiper) {
    if (pendingPositionRef.current !== null) return;
    setVisibleSlideIndex(swiper.activeIndex);
    if (!useNativeScroll) setActiveSlide(swiper.activeIndex);
  }

  function onTouchStart() {
    // A user gesture takes priority over an entry position still waiting for
    // React's virtual slide to mount.
    if (useNativeScroll) cancelPendingPosition();
  }

  function onSetTranslate(swiper) {
    if (!useNativeScroll) return;

    if (pendingPositionRef.current !== null) return;

    setVisibleSlideIndex(swiper.activeIndex);
    // Native momentum can cross several slides. Commit the visible artwork
    // only after scrolling and scroll snapping have settled.
    cancelNativeCommit();
    nativeScrollCommitTimerRef.current = window.setTimeout(() => {
      nativeScrollCommitTimerRef.current = null;
      nativeCommittedSlideRef.current = swiper.activeIndex;
      setActiveSlide(swiper.activeIndex);
    }, 120);
  }

  return { visibleSlideIndex, onInit, onVirtualUpdate, onSlideChange, onTouchStart, onSetTranslate };
}
